import React, { useState, useCallback, useEffect } from 'react';
import './BetaTestPage.css';
import { Footer, Header, Button, UnderlinedText, LegalDocumentModal } from '../shared';
import { SocialMediaSection } from '../sections';
import { useLanguage } from '../../contexts/LanguageContext';

// Images
import betaTestBg from '../../assets/girl_looking_phone.png';
import universeBg from '../../assets/girl_talking_big_image.png';

/**
 * Beta Test Page Component
 * Dedicated page for beta test sign-up with:
 * - Hero section with beta test info
 * - Guideline section explaining the process
 * - Registration form
 * - Social media and footer sections
 *
 * @param {function} onNavigateHome - Callback to navigate back to home page
 */
const BetaTestPage = ({ onNavigateHome }) => {
  const { t } = useLanguage();

  // State for legal document modal
  const [openLegalDocument, setOpenLegalDocument] = useState(null);

  // Handle hash-based scrolling when component mounts (for navigation from main page)
  useEffect(() => {
    if (window.location.hash === '#registration-form') {
      // Wait for page to fully render including images before scrolling
      const scrollToRegistration = () => {
        const registrationAnchor = document.getElementById('registration-form');
        if (registrationAnchor) {
          registrationAnchor.scrollIntoView({ behavior: 'smooth' });
        }
      };

      // Longer delay to ensure all content including images is loaded
      setTimeout(scrollToRegistration, 800);
    }
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    university: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Callbacks for legal documents
  const handleOpenLegalDocument = useCallback((documentType) => {
    setOpenLegalDocument(documentType);
  }, []);

  const handleCloseLegalDocument = useCallback(() => {
    setOpenLegalDocument(null);
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};

    if (!formData.university) {
      errors.university = t('betaTestPage.registration.universityPlaceholder');
    }

    if (!formData.email) {
      errors.email = t('betaTestPage.registration.emailPlaceholder');
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    // Issue #344: Call backend API for beta registration
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/beta-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          university: formData.university,
          email: formData.email,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        setFormData({ university: '', email: '' });
      } else {
        // Issue #348 / #392: Map backend error codes to localized messages.
        let errorMessage;
        switch (data.message) {
          case 'email_already_registered':
            errorMessage = t('betaTestPage.registration.emailAlreadyRegistered');
            break;
          case 'beta_invalid_domain':
            errorMessage = t('betaTestPage.registration.emailInvalidDomain');
            break;
          case 'beta_email_send_failed':
            errorMessage = t('betaTestPage.registration.emailSendFailed');
            break;
          case 'beta_university_mismatch':
            // Issue #397: selected university does not match the student email domain.
            errorMessage = t('betaTestPage.registration.emailUniversityMismatch');
            break;
          default:
            errorMessage = data.message || 'Registration failed. Please try again.';
        }
        setFormErrors({
          email: errorMessage,
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setFormErrors({
        email: 'Network error. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Guideline steps data
  const guidelineSteps = [
    {
      id: 'registration',
      title: t('betaTestPage.guideline.step1.title'),
      description: t('betaTestPage.guideline.step1.description'),
      image: '/images/registration.jpg',
      imageAlt: t('a11y.registrationImageAlt'),
      reversed: false
    },
    {
      id: 'email',
      title: t('betaTestPage.guideline.step2.title'),
      description: t('betaTestPage.guideline.step2.description'),
      image: '/images/email_notification_without_background.png',
      imageAlt: t('a11y.emailNotificationImageAlt'),
      reversed: true
    },
    {
      id: 'explore',
      title: t('betaTestPage.guideline.step3.title'),
      description: t('betaTestPage.guideline.step3.description'),
      image: '/images/explore_without_background.png',
      imageAlt: t('a11y.exploreImageAlt'),
      reversed: false
    },
    {
      id: 'feedback',
      title: t('betaTestPage.guideline.step4.title'),
      description: t('betaTestPage.guideline.step4.description'),
      image: '/images/amazon_gift_card.png',
      imageAlt: t('a11y.amazonGiftCardImageAlt'),
      reversed: true,
      hasRaffle: true,
      raffleTitle: t('betaTestPage.guideline.step4.raffleTitle'),
      raffleDescription: t('betaTestPage.guideline.step4.raffleDescription')
    }
  ];

  return (
    <div className="beta-test-page">
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>

      {/* Hero Section with Header */}
      <div
        className="beta-hero-wrapper"
        style={{ backgroundImage: `url(${betaTestBg})` }}
      >
        {/* Dark overlay */}
        <div className="beta-hero-overlay" aria-hidden="true"></div>

        {/* Header with home link.
            Issue #423: the beta CTA is intentionally NOT passed here — on the
            beta-test page itself the button would have nowhere meaningful to
            go, so we hide it by omitting onBetaClick. */}
        <Header onLogoClick={onNavigateHome} />

        {/* Hero Content */}
        <section className="beta-hero" aria-labelledby="beta-hero-heading">
          <div className="beta-hero-container">
            <div className="beta-hero-content">
              <div className="beta-hero-text-block">
                <h1 id="beta-hero-heading" className="beta-hero-title">
                  {t('betaTestPage.hero.titleLine1Start')}
                  <UnderlinedText textColor="#f6b707" showUnderline={false}>
                    {t('betaTestPage.hero.titleLine1Highlight')}
                  </UnderlinedText>
                  <br />
                  {t('betaTestPage.hero.titleLine2')}
                </h1>
                <p className="beta-hero-description">
                  {t('betaTestPage.hero.description')}
                </p>
              </div>
              <Button
                variant="primary"
                size="large"
                ariaLabel={t('betaTestPage.hero.ctaButton')}
                href="#registration-form"
              >
                {t('betaTestPage.hero.ctaButton')}
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Main Content */}
      <main id="main-content" role="main">
        {/* Guideline Section */}
        <section className="beta-guideline" aria-labelledby="guideline-heading">
          <div className="beta-guideline-container">
            <h2 id="guideline-heading" className="beta-guideline-title">
              {t('betaTestPage.guideline.title')}
            </h2>

            {/* Guideline Steps */}
            {guidelineSteps.map((step) => (
              <div
                key={step.id}
                className={`beta-guideline-step ${step.reversed ? 'beta-guideline-step--reversed' : ''}`}
              >
                <div className="beta-guideline-step-image">
                  <img
                    src={step.image}
                    alt={step.imageAlt}
                  />
                </div>
                <div className="beta-guideline-step-content">
                  <h3 className="beta-guideline-step-title">
                    <UnderlinedText color="yellow">{step.title}</UnderlinedText>
                  </h3>
                  <p className="beta-guideline-step-description">
                    {step.description}
                  </p>
                  {step.hasRaffle && (
                    <div className="beta-guideline-raffle">
                      <h4 className="beta-guideline-raffle-title">
                        {step.raffleTitle}
                      </h4>
                      <p className="beta-guideline-raffle-description">
                        {step.raffleDescription}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Registration Form Section */}
        <section
          className="beta-registration"
          aria-labelledby="registration-heading"
        >
          <div className="beta-registration-container">
            {/* Scroll anchor for navigation - positioned at actual content start */}
            <span id="registration-form" className="sr-only" aria-hidden="true"></span>
            <h2 id="registration-heading" className="beta-registration-title">
              {t('betaTestPage.registration.title')}
            </h2>

            {submitSuccess ? (
              <div className="beta-registration-success" role="alert">
                <p>{t('betaTestPage.registration.successMessage')}</p>
              </div>
            ) : (
              <form
                className="beta-registration-form"
                onSubmit={handleSubmit}
                noValidate
              >
                {/* University Select */}
                <div className="beta-form-group">
                  <label
                    htmlFor="university"
                    className="beta-form-label"
                  >
                    {t('betaTestPage.registration.universityLabel')}
                  </label>
                  <select
                    id="university"
                    name="university"
                    className={`beta-form-select ${formErrors.university ? 'beta-form-select--error' : ''}`}
                    value={formData.university}
                    onChange={handleInputChange}
                    aria-describedby={formErrors.university ? 'university-error' : undefined}
                    aria-invalid={formErrors.university ? 'true' : 'false'}
                  >
                    <option value="">
                      {t('betaTestPage.registration.universityPlaceholder')}
                    </option>
                    <option value="tuhh">
                      {t('betaTestPage.registration.universities.tuhh')}
                    </option>
                    <option value="hawKiel">
                      {t('betaTestPage.registration.universities.hawKiel')}
                    </option>
                  </select>
                  {formErrors.university && (
                    <span id="university-error" className="beta-form-error" role="alert">
                      {formErrors.university}
                    </span>
                  )}
                </div>

                {/* Email Input */}
                <div className="beta-form-group">
                  <label
                    htmlFor="email"
                    className="beta-form-label"
                  >
                    {t('betaTestPage.registration.emailLabel')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`beta-form-input ${formErrors.email ? 'beta-form-input--error' : ''}`}
                    placeholder={t('betaTestPage.registration.emailPlaceholder')}
                    value={formData.email}
                    onChange={handleInputChange}
                    aria-describedby={formErrors.email ? "email-error" : undefined}
                    aria-invalid={formErrors.email ? 'true' : 'false'}
                    autoComplete="email"
                  />
                  {formErrors.email && (
                    <span id="email-error" className="beta-form-error" role="alert">
                      {formErrors.email}
                    </span>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  variant="primary"
                  size="large"
                  type="submit"
                  disabled={isSubmitting}
                  ariaLabel={t('betaTestPage.registration.submitButton')}
                >
                  {isSubmitting ? '...' : t('betaTestPage.registration.submitButton')}
                </Button>
              </form>
            )}
          </div>
        </section>
      </main>

      {/* Bottom sections wrapper with shared universe background */}
      <div
        className="bottom-wrapper"
        style={{ backgroundImage: `url(${universeBg})` }}
      >
        {/* Dark overlay for better text readability */}
        <div className="bottom-wrapper-overlay" aria-hidden="true"></div>

        {/* Social Media Section */}
        <SocialMediaSection />

        {/* Footer */}
        <Footer onOpenLegalDocument={handleOpenLegalDocument} />
      </div>

      {/* Legal Document Modal */}
      <LegalDocumentModal
        documentType={openLegalDocument}
        isOpen={openLegalDocument !== null}
        onClose={handleCloseLegalDocument}
      />
    </div>
  );
};

export default BetaTestPage;
